import {
  SubscriptionFlowDefinition,
  type FlowAnswers,
  type CrossSellConfig,
  type RecapPolicy,
} from './SubscriptionFlowDefinition'
import type { AvailableBundlesParams } from '@/services/subscription'

/**
 * Health top-up journey (`/renforcer-sante`, subcategory SURCOMPL_SANTE).
 *
 * The decision tree (DecisionTree::SurcomplSante) ignores every input and always
 * returns the single Tonic-Études bundle, so `available_bundles` only needs the
 * subcategory. The chosen "plan" is Tonic-Études itself; the cross-sell add-ons
 * (Sécurité / Indispensable) come from that bundle's `upsell_bundles`, priced as
 * upsell − base — the same mechanism as the health-expenses flow. Choosing an
 * add-on bundles a free "exam insurance" line.
 *
 * An annual cover paid upfront or monthly, renewing tacitly, with the STUD'LIFE
 * club reassurance block on the recap. The period is the standard start date over
 * `duration` months (fixed to 12 here) and the price is the standard
 * monthly/annual rate, so neither `period()` nor `displayPrice()` is overridden.
 */
export class HealthTopUpFlow extends SubscriptionFlowDefinition {
  readonly product = 'healthTopUp' as const
  readonly subcategory = 'SURCOMPL_SANTE' as const

  /** Source of truth: DecisionTree::SurcomplSante on the backend (Tonic-Études). */
  readonly planReference: Record<string, number> = {
    tonic: 8,
  }

  /** Yearly-renewing cover paid upfront or monthly; shows the club block. */
  readonly recap: RecapPolicy = {
    showPaymentSelector: true,
    showClub: true,
    tacitRow: 'yes',
    priceMode: 'duration',
  }

  /** AVE add-ons (Sécurité / Indispensable) from the Tonic bundle's upsells;
   *  choosing one bundles a free exam-insurance line. */
  readonly crossSell: CrossSellConfig = { answerKey: 'crossSell', bundlesExamInsurance: true }

  bundlesParams(a: FlowAnswers): AvailableBundlesParams | null {
    // The tree needs no answer, but we wait until the questionnaire reaches the
    // results step (age answered) before querying, mirroring the other flows.
    if (!a.age) return null
    return { subcategory: 'SURCOMPL_SANTE' }
  }
}
